import { createSlice } from '@reduxjs/toolkit'

import { logger } from 'src/utils/logger'

import {
  login,
  ssoLogin,
  mfaLogin,
  providerLogin,
  rememberAuth,
  userSettingGet,
  userSettingPut,
  getProvider,
  getProviderFavCPT,
  getProviderFavDX,
  getProviderCodeTemplates,
} from './userThunk'

const initialState = {
  authToken: null,
  authRefreshToken: null,
  mfa_token: null,
  errorMsg: '',
  practice: null,
  provider: null,
  providerFavCPT: {},
  providerFavDX: {},
  providerCodeTemplates: [],
  practiceList: [],
  providerList: [],
  isProviderLogin: false,
  roles: null,
  permissions: null,
  settings: {
    theme: localStorage.getItem('theme'),
  },
  features: null,
  user: null,
}

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setPractice: (state, action) => {
      state.practice = action.payload
    },
    userLogout: (state) => {
      localStorage.clear()
      state.authToken = initialState.authToken
      state.authRefreshToken = initialState.authRefreshToken
      state.practice = initialState.practice
      state.provider = initialState.provider
      state.providerFavCPT = initialState.providerFavCPT
      state.providerFavDX = initialState.providerFavDX
      state.providerCodeTemplates = initialState.providerCodeTemplates
      state.practiceList = initialState.practiceList
      state.providerList = initialState.providerList
      state.isProviderLogin = initialState.isProviderLogin
      state.roles = initialState.roles
      state.permissions = initialState.permissions
      state.settings = initialState.settings
      state.features = initialState.features
      state.user = initialState.user
      state.census = initialState.census
      if (!window.location.href?.includes('#/login')) {
        window.location.replace('/')
      }
    },
    setTheme: (state, action) => {
      if (state?.settings) {
        state.settings.theme = action.payload
        localStorage.setItem('theme', action.payload)
      }
    },
    acceptTos: (state) => {
      state.user.tosAccepted = true

      // update authObject
      const authUser = JSON.parse(localStorage.getItem('authUser'))
      authUser.user.tosAccepted = true

      localStorage.setItem('authUser', JSON.stringify(authUser))
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(login.fulfilled, (state, action) => {
        if (action.payload?.mfa_token) {
          state.mfa_token = action.payload?.mfa_token
          state.username = action.payload?.username
        } else {
          state.authToken = action.payload?.access_token
          state.authRefreshToken = action.payload?.refresh_token
          state.user = action.payload?.user
          setProviderAndPractice(state, action)
        }
      })
      /**
       * Handles successful SSO login completion
       * Stores authentication tokens, user information, and initializes practice/provider data
       *
       * @param {Object} state - Current Redux state
       * @param {Object} action - Redux action with payload from ssoLogin thunk
       * @param {string} action.payload.access_token - JWT access token
       * @param {string} action.payload.refresh_token - JWT refresh token
       * @param {Object} action.payload.user - User information object
       * @param {Array} action.payload.practiceList - Available practices
       * @param {Array} action.payload.providerList - Available providers
       */
      .addCase(ssoLogin.fulfilled, (state, action) => {
        state.authToken = action.payload?.access_token
        state.authRefreshToken = action.payload?.refresh_token
        state.user = action.payload?.user
        setProviderAndPractice(state, action)
      })
      /**
       * Handles SSO login failure
       * Stores error message in state for display to user
       *
       * @param {Object} state - Current Redux state
       * @param {Object} action - Redux action with error payload
       * @param {string} action.payload.message - Error message describing the failure
       */
      .addCase(ssoLogin.rejected, (state, action) => {
        logger.error('[SSO Login] Rejected:', action.payload)
        state.errorMsg = action.payload?.message || 'SSO login failed'
      })
      .addCase(getProvider.fulfilled, (state, action) => {
        if (action.payload) {
          state.provider = action.payload
        }
      })
      .addCase(mfaLogin.fulfilled, (state, action) => {
        if (action.payload?.access_token) {
          state.mfa_token = null
          state.authToken = action.payload?.access_token
          state.authRefreshToken = action.payload?.refresh_token
          state.user = action.payload?.user
          state.roles = action.payload?.roles
          state.permissions = action.payload?.permissions
          setProviderAndPractice(state, action)
        }
      })
      .addCase(mfaLogin.rejected, (state, action) => {
        console.error(action.payload)
      })
      .addCase(providerLogin.pending, (state, action) => {
        state.settings = initialState.settings
      })
      .addCase(providerLogin.fulfilled, (state, action) => {
        state.provider = action.payload?.providerRes
        state.practice = action.payload?.practiceRes
        state.isProviderLogin = true
        state.roles = action.payload?.rolesRes
        state.settings = action.payload?.settingsRes
        state.features = action.payload?.featuresRes
        state.permissions = action.payload?.permissionsRes
        state.providerFavCPT = action.payload?.favCPTs || {}
        state.providerFavDX = action.payload?.favDX || {}
        state.providerCodeTemplates = action.payload?.provCodeTemplates || []
      })
      .addCase(providerLogin.rejected, (state, action) => {
        console.error(action.payload)
      })
      .addCase(getProviderFavCPT.fulfilled, (state, action) => {
        state.providerFavCPT = action.payload || {}
      })
      .addCase(getProviderFavDX.fulfilled, (state, action) => {
        state.providerFavDX = action.payload || {}
      })
      .addCase(getProviderCodeTemplates.fulfilled, (state, action) => {
        state.providerCodeTemplates = action.payload || []
      })
      .addCase(userSettingGet.fulfilled, (state, action) => {
        state.settings = action.payload?.settingsRes
      })
      .addCase(userSettingGet.rejected, (state, action) => {
        console.error(action.payload)
      })
      .addCase(userSettingPut.fulfilled, (state, action) => {
        if (action.payload) {
          state.settings = action.payload
        }
      })
      .addCase(userSettingPut.rejected, (state, action) => {
        console.error(action.payload)
      })
      .addCase(rememberAuth.fulfilled, (state, action) => {
        state.user = action.payload?.user
        state.authToken = action.payload?.authToken
        state.authRefreshToken = action.payload?.authRefreshToken
        state.providerList = action.payload?.providerRes || []
        state.practiceList = action.payload?.practiceRes
        state.provider = action.payload?.currentProviderRes
        state.practice = action.payload?.currentPracticeRes
        state.roles = action.payload?.rolesRes
        state.settings = action.payload?.settingsRes
        state.features = action.payload?.featuresRes
        state.permissions = action.payload?.permissionsRes
      })
      .addCase(rememberAuth.rejected, (state, action) => {
        console.error(action.payload)
        userSlice.caseReducers.userLogout(state)
      })
  },
})

const setProviderAndPractice = (state, action) => {
  state.providerList = action.payload?.providerList || []
  state.practiceList = action.payload?.practiceList?.filter((item, i) => {
    return item?.enabled === true
  })
  if (state.practiceList?.length === 1) {
    state.practice = state.practiceList[0]
    let filteredProviders = state.providerList.filter((item, i) => {
      return item.practice_id === state.practice?.practice_id
    })
    if (filteredProviders?.length === 1) {
      state.provider = filteredProviders[0]
    } else if (filteredProviders?.length === 0) {
      state.provider = null
    }
  }
}

export const { setPractice, userLogout, setTheme, acceptTos } = userSlice.actions
export const selectUser = (state) => state.user
export const getTheme = (state) => 'dark' || state?.user?.settings?.theme

const msInADay = 1000 * 60 * 60 * 24
export const shouldShowAsteriskWarning = (state) => {
  let asteriskDate = state?.user?.settings?.asteriskWarningExpiration
  if (!asteriskDate) {
    return true
  }

  // do not show warning if we are within 30 day period
  const diff = Math.abs(Date.now() - new Date(asteriskDate).getTime())
  return diff / msInADay >= 30
}
export default userSlice.reducer
